// client/src/dnd/emitter_tool/hooks/useTargetingHost.js
import { useCallback, useEffect, useRef } from "react";
import installTargetingManager from "../engine/TargetingManager";

/**
 * Stable host ref + TargetingManager lifecycle.
 * - Keeps the callback ref identity stable to avoid unmount/remount churn.
 * - Uses internal refs for getOrigin / setters so they stay fresh without
 *   forcing a new ref function on every render.
 */
export default function useTargetingHost({
  getOrigin = () => ({ x: 0, y: 0 }),
  setMousePx = () => {},
  setGuidePx = () => {},
} = {}) {
  const managerRef = useRef(null);
  const hostElRef = useRef(null);

  // keep the latest functions without changing the ref identity
  const getOriginRef = useRef(getOrigin);
  const setMousePxRef = useRef(setMousePx);
  const setGuidePxRef = useRef(setGuidePx);

  useEffect(() => {
    getOriginRef.current = getOrigin;
  }, [getOrigin]);

  useEffect(() => {
    setMousePxRef.current = setMousePx;
  }, [setMousePx]);

  useEffect(() => {
    setGuidePxRef.current = setGuidePx;
  }, [setGuidePx]);

  // STABLE ref function: does not change across renders
  const hostRef = useCallback((el) => {
    if (hostElRef.current === el) return;

    // destroy previous manager if any
    try { managerRef.current?.destroy?.(); } catch {}
    managerRef.current = null;
    hostElRef.current = el;

    if (el) {
      managerRef.current = installTargetingManager({
        host: el,
        getOrigin: () => {
          try {
            const fn = getOriginRef.current;
            return typeof fn === "function" ? fn() : { x: 0, y: 0 };
          } catch {
            return { x: 0, y: 0 };
          }
        },
      });
    }
  }, []); // ← empty deps keeps this callback stable

  // Listen for UI events and update state via *refs* (no re-installs)
  useEffect(() => {
    const onMouse = (e) => {
      const p = e?.detail;
      if (!p || typeof p.x !== "number" || typeof p.y !== "number") return;
      try { setMousePxRef.current({ x: p.x, y: p.y }); } catch {}
    };
    const onGuide = (e) => {
      try { setGuidePxRef.current(e?.detail ?? null); } catch {}
    };

    window.addEventListener("EmitterTool:UI:MouseMoved", onMouse);
    window.addEventListener("EmitterTool:UI:GuideMoved", onGuide);
    return () => {
      window.removeEventListener("EmitterTool:UI:MouseMoved", onMouse);
      window.removeEventListener("EmitterTool:UI:GuideMoved", onGuide);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try { managerRef.current?.destroy?.(); } catch {}
      managerRef.current = null;
      hostElRef.current = null;
    };
  }, []);

  return { hostRef };
}
